/**
 * Content entitlement · SINGLE SOURCE OF TRUTH for "may this user receive this
 * sprint's content?" (slot_content_protection Phase 1 · 29 JUN 2026 · Genie).
 *
 * Lives in `shared/` so BOTH the client (UX gating) and the edge content function
 * (the REAL gate · mirrored via sync-edge-shared) use identical rules — no drift
 * in a security boundary. `src/lib/sprint-progression.ts` now re-exports the
 * progression helpers from here.
 *
 * Pure: depends only on static course data (SPRINTS, ticket meta, quiz banks) +
 * the user's Progress + role. No DOM, no localStorage, no Deno/browser globals →
 * runs unchanged in Node (vitest), the browser, and Deno (edge).
 *
 * The DEV unlock-all flag is CLIENT-only (localStorage) and is passed in as a
 * parameter (`devUnlockAll`) — the server never trusts it; server-side, an admin
 * role is what grants unlock-all (covers the admin dev-mode intent).
 */

import type { Progress, UserRole } from '../schemas';
import { SPRINTS } from '../data/sprints';
import { ticketsBySprint } from '../data/tickets';
import type { TicketMeta } from '../schemas';
import { maxOrdForRole } from './roles';
import { QUIZ_BANK_PASS_PCT } from '../data/quiz-bank-ids';

/**
 * Highest sprint ord a `trial` user may EVER receive content for. Kept as a named
 * export for back-compat (tests + call sites) but now DERIVED from the canonical
 * role→cap table in `roles.ts` — the trial cap lives in exactly one place.
 */
export const TRIAL_MAX_SPRINT_ORD = maxOrdForRole('trial');

const isMissionTicket = (t: TicketMeta): boolean => t.type === 'mission';

const requiredTicketsForSprint = (sprintId: string): TicketMeta[] =>
  ticketsBySprint(sprintId).filter((t) => !isMissionTicket(t));

const ordOf = (sprintId: string): number | undefined =>
  SPRINTS.find((s) => s.id === sprintId)?.ord;

/**
 * True when a sprint counts as complete for progression (unlocks the next).
 * Sprint with a final-test bank → complete only when `progress.sprints[id].status
 * === 'completed'` (set on a passing score); otherwise all required tickets done.
 * (Mirrors the pre-29-JUN logic verbatim · sprint-progression.ts.)
 */
export function isSprintComplete(
  progress: Pick<Progress, 'sprints' | 'tickets'>,
  sprintId: string,
): boolean {
  // `finalTestBankId` (light · sprints.ts) rather than importing the heavy
  // quiz-banks aggregator — keeps the edge content function's cold-compile graph
  // small (slot_content_protection · 29 JUN).
  const bankId = SPRINTS.find((s) => s.id === sprintId)?.finalTestBankId;
  if (bankId) {
    // Completeness is NOT hung on a single field (ADR-019 · P5). A corrupted
    // `status` (e.g. a stale-snapshot storm rolling completed→available) must not
    // lock a paid student out of a sprint they already passed. A passing
    // `finalTestScore` — a pct written on submit alongside status + completedAt,
    // and which survives that exact corruption — also counts as complete. The
    // pass threshold (pct) comes from the bank's own passThreshold, via the light
    // QUIZ_BANK_PASS_PCT map (drift-guarded), NOT a hardcode.
    const sp = progress.sprints[sprintId];
    if (sp?.status === 'completed') return true;
    const thresholdPct = QUIZ_BANK_PASS_PCT[bankId] ?? 101; // unknown bank → status-only
    return (sp?.finalTestScore ?? 0) >= thresholdPct;
  }
  const required = requiredTicketsForSprint(sprintId);
  return required.length > 0 && required.every((t) => progress.tickets[t.id]?.status === 'done');
}

/**
 * True when a sprint is locked behind an earlier incomplete REQUIRED sprint
 * (optional sprints don't gate). `devUnlockAll` (client dev toggle) bypasses —
 * defaults false so the server NEVER honours a client flag.
 */
export function isSprintLocked(
  progress: Progress,
  sprintId: string,
  devUnlockAll = false,
): boolean {
  if (devUnlockAll) return false;
  const target = SPRINTS.find((s) => s.id === sprintId);
  if (!target) return true;
  for (const s of SPRINTS) {
    if (s.ord >= target.ord) break;
    if (s.optional) continue;
    if (!isSprintComplete(progress, s.id)) return true;
  }
  return false;
}

/**
 * THE content gate. May the user receive this sprint's body/quiz content?
 * Stricter than the client UX rules (no peek-ahead): only sprints the user has
 * actually reached (current + completed, per linear progression), capped by the
 * role's tier ceiling (`maxOrdForRole` · the SINGLE SOURCE OF TRUTH in roles.ts).
 *
 *   admin      → every sprint (uncapped · covers admin dev-mode unlock-all).
 *   pro        → reached sprints ≤ S19. free/start/manualplus → reached sprints
 *                within their cap. In all non-admin cases: no future, no peek.
 *
 * The tier cap fires BEFORE the linear-lock check, so a role NEVER receives
 * content past its ceiling regardless of progress or the (ignored) dev flag.
 *
 * @param devUnlockAll client-only dev toggle; IGNORE on the server (pass false).
 */
export function isContentEntitled(
  role: UserRole,
  progress: Progress,
  sprintId: string,
  devUnlockAll = false,
): boolean {
  const ord = ordOf(sprintId);
  if (ord === undefined) return false;
  if (ord > maxOrdForRole(role)) return false; // tier ceiling (admin = Infinity → never)
  if (role === 'admin') return true; // admin bypasses linear progression (server-side dev-mode)
  return !isSprintLocked(progress, sprintId, devUnlockAll);
}
